import { useCallback, useEffect, useRef, useState } from 'react';
import { useQueryClient } from '@tanstack/react-query';
import { useToast } from '@/shared/ui/Toast';
import { integrationsApi } from '@/modules/settings/api/integrationsApi';
import { integrationKeys } from '@/modules/settings/queryKeys';

/**
 * Trigger a platform sync (Google + Meta) from anywhere in the Ads UI, and keep
 * the campaign views refreshing while it lands.
 *
 * The sync itself is a queued background job — the trigger endpoints return as
 * soon as it's enqueued, and a worker updates campaign inventory, names,
 * statuses and metrics seconds later. So rather than refetch once (which would
 * show stale data), this refreshes the campaign queries a few times over the
 * following minute, and shows a spinner for that window. Per-platform failures
 * (e.g. a disconnected platform) are ignored as long as one sync started.
 */
const REFRESH_AT_MS = [6000, 15000, 30000, 45000, 60000];

export function useTriggerAdsSync() {
  const queryClient = useQueryClient();
  const toast = useToast();
  const [syncing, setSyncing] = useState(false);
  const timers = useRef<ReturnType<typeof setTimeout>[]>([]);

  const clearTimers = useCallback(() => {
    timers.current.forEach(clearTimeout);
    timers.current = [];
  }, []);

  useEffect(() => () => clearTimers(), [clearTimers]);

  const refresh = useCallback(() => {
    // Prefix-invalidate every campaign-facing query: the overview table (`ads`),
    // the marketing-campaign list and performance views (`campaigns`).
    void queryClient.invalidateQueries({ queryKey: ['ads'] });
    void queryClient.invalidateQueries({ queryKey: ['campaigns'] });
    // Per-platform "last synced / Sync failed" lines on the ads page.
    void queryClient.invalidateQueries({ queryKey: integrationKeys.googleAdsStatusAll() });
    void queryClient.invalidateQueries({ queryKey: integrationKeys.metaAdsStatus() });
  }, [queryClient]);

  const syncNow = useCallback(async () => {
    if (syncing) return;
    setSyncing(true);
    clearTimers();

    const results = await Promise.allSettled([
      integrationsApi.googleAds.triggerSync(),
      integrationsApi.metaAds.triggerSync(),
    ]);
    if (!results.some((r) => r.status === 'fulfilled')) {
      setSyncing(false);
      toast.error('Could not start a sync — check your ad connections in Settings → Integrations.');
      return;
    }

    toast.info(
      'Sync started — pulling the latest from Google and Meta. This runs in the background and can take up to a minute.',
    );

    for (const at of REFRESH_AT_MS) {
      timers.current.push(setTimeout(refresh, at));
    }
    timers.current.push(
      setTimeout(() => {
        setSyncing(false);
        toast.success('Ad campaigns refreshed.');
      }, REFRESH_AT_MS[REFRESH_AT_MS.length - 1] + 1000),
    );
  }, [syncing, clearTimers, refresh, toast]);

  return { syncNow, syncing };
}
